import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  FolderKanban,
  Loader2,
  Package,
  Plus,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { ViewToggleButton } from "@/components/ui/ViewToggleButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { RowActions } from "@/components/ui/RowActions";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { initials } from "@/lib/utils";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList, useDeleteProject } from "../hooks/useProjects";
import { NewProjectDrawer } from "../components/NewProjectDrawer";
import { useViewMode } from "@/hooks/useViewMode";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type { ProjectListItem, ProjectStatus } from "../types";

type StatusFilter = "" | ProjectStatus;
type TypeFilter = string;

const PROJECT_COLUMNS: ColumnDef[] = [
  { key: "name", label: "Name + Code" },
  { key: "type", label: "Type" },
  { key: "companyName", label: "Company" },
  { key: "productName", label: "Product" },
  { key: "status", label: "Status" },
  { key: "progress", label: "Progress" },
  { key: "managerName", label: "Manager", defaultVisible: false },
];

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    PLANNING: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)", label: "Planning" },
    ACTIVE: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)", label: "Active" },
    ON_HOLD: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "On Hold" },
    COMPLETED: { bg: "hsl(175,55%,92%)", fg: "hsl(175,60%,30%)", label: "Completed" },
    CANCELLED: { bg: "hsl(0,84%,95%)", fg: "hsl(0,72%,45%)", label: "Cancelled" },
  };
  const s = styles[status] ?? styles.PLANNING;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

// Color palette keyed by lowercase dropdown value. Falls back to neutral for
// admin-added types (the Dropdown Manager carries no color metadata).
const TYPE_BADGE_COLORS: Record<string, { bg: string; fg: string }> = {
  poc: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  pilot: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)" },
  production: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)" },
  internal: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)" },
};
const NEUTRAL_TYPE_BADGE = { bg: "hsl(210,30%,94%)", fg: "hsl(215,20%,35%)" };

function TypeBadge({
  type,
  label,
}: {
  type: string | null;
  label?: string;
}) {
  if (!type) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const c = TYPE_BADGE_COLORS[type] ?? NEUTRAL_TYPE_BADGE;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: c.bg, color: c.fg }}
    >
      {label ?? type}
    </span>
  );
}

function ProgressBar({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 rounded-full bg-[hsl(210,16%,93%)]">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${Math.min(100, value)}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground">{value}%</span>
    </div>
  );
}

function ProjectRow({
  project,
  onDelete,
  isVisible,
  typeLabel,
}: {
  project: ProjectListItem;
  onDelete: (id: string, name: string) => void;
  isVisible: (key: string) => boolean;
  typeLabel?: string;
}) {
  const navigate = useNavigate();
  return (
    <tr
      className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
      onClick={() => navigate(`/projects/${project.id}`)}
    >
      {isVisible("name") && (
        <td className="px-6 py-4">
          <div className="font-medium text-foreground">{project.name}</div>
          <div className="mt-0.5 font-mono text-xs text-muted-foreground">
            {project.code}
          </div>
        </td>
      )}
      {isVisible("type") && (
        <td className="px-6 py-4">
          <TypeBadge type={project.type} label={typeLabel} />
        </td>
      )}
      {isVisible("companyName") && (
        <td className="px-6 py-4">
          {project.companyName ? (
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/companies/${project.companyId}`);
              }}
            >
              <Building2 className="h-3 w-3" strokeWidth={1.5} />
              {project.companyName}
            </button>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </td>
      )}
      {isVisible("productName") && (
        <td className="px-6 py-4">
          {project.productName ? (
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/products/${project.productId}`);
              }}
            >
              <Package className="h-3 w-3" strokeWidth={1.5} />
              {project.productName}
            </button>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </td>
      )}
      {isVisible("status") && (
        <td className="px-6 py-4">
          <StatusBadge status={project.status} />
        </td>
      )}
      {isVisible("progress") && (
        <td className="px-6 py-4">
          <ProgressBar value={project.progress} />
        </td>
      )}
      {isVisible("managerName") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {project.managerName ?? "—"}
        </td>
      )}
      <td className="px-6 py-4">
        <RowActions
          onView={() => navigate(`/projects/${project.id}`)}
          onEdit={() => navigate(`/projects/${project.id}?edit=true`)}
          onDelete={() => onDelete(project.id, project.name)}
        />
      </td>
    </tr>
  );
}

function ProjectCard({
  project,
  typeLabel,
}: {
  project: ProjectListItem;
  typeLabel?: string;
}) {
  const navigate = useNavigate();
  return (
    <div
      className="cursor-pointer rounded-xl border bg-card p-5 transition-colors hover:bg-[hsl(210,50%,98%)]"
      style={{ borderColor: "hsl(210,30%,85%)" }}
      onClick={() => navigate(`/projects/${project.id}`)}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(262,55%,92%)] text-sm font-semibold text-[hsl(262,83%,38%)]">
          {initials(project.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground">{project.name}</div>
          <div className="mt-0.5 font-mono text-xs text-muted-foreground">
            {project.code}
          </div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <TypeBadge type={project.type} label={typeLabel} />
        <StatusBadge status={project.status} />
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Building2 className="h-3 w-3" strokeWidth={1.5} />
        {project.companyName ?? "—"}
        {project.productName && (
          <>
            <span className="text-muted-foreground/50">›</span>
            <Package className="h-3 w-3" strokeWidth={1.5} />
            {project.productName}
          </>
        )}
      </div>
      <div className="mt-3">
        <ProgressBar value={project.progress} />
      </div>
    </div>
  );
}

export default function ProjectListPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const { mode: viewMode, toggle: toggleViewMode, isGrid } = useViewMode(
    "projects",
    "list",
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    visibleKeys,
    isVisible,
    minReached,
  } = useColumnVisibility("projects", PROJECT_COLUMNS);
  const exportFilters = useMemo(
    () => ({
      search: search.trim() || undefined,
      status: statusFilter || undefined,
      type: typeFilter || undefined,
      companyId: companyFilter || undefined,
      productId: productFilter || undefined,
    }),
    [search, statusFilter, typeFilter, companyFilter, productFilter],
  );

  const deleteProject = useDeleteProject();

  const { data: companiesResult } = useCompanyList({ page: 1, limit: 100, operationalOnly: true });
  const allCompanies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({ page: 1, limit: 100 });
  const allProducts = productsResult?.data ?? [];

  const { data: projectTypeOptions = [] } = useCategoryItems("project_type");
  const typeLabelByValue = useMemo(
    () => new Map(projectTypeOptions.map((t) => [t.value, t.label])),
    [projectTypeOptions],
  );

  const {
    data: result,
    isLoading,
    isError,
    error,
  } = useProjectList({
    page,
    limit,
    search: search.trim() || undefined,
    status: statusFilter || undefined,
    type: typeFilter || undefined,
    companyId: companyFilter || undefined,
    productId: productFilter || undefined,
  });

  const projects = result?.data ?? [];
  const pagination = result?.pagination ?? {
    page: 1,
    limit,
    total: 0,
    totalPages: 1,
  };
  const total = pagination.total;

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function handleDelete(id: string, name: string) {
    setDeleteTarget({ id, name });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteProject.mutateAsync(deleteTarget.id);
    showToast(`${deleteTarget.name} deleted successfully`);
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Business Structure" },
        { label: "Projects" },
      ]}
    >
      <div className="w-full space-y-6">
        {/* Toast */}
        {toast && (
          <div
            className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
            style={{ borderColor: "hsl(142,55%,80%)" }}
          >
            {toast}
          </div>
        )}

        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FolderKanban
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
          </div>
          <div className="flex items-center gap-2">
            <ViewToggleButton mode={viewMode} onToggle={toggleViewMode} />
            <ExportButton
              module="projects"
              filters={exportFilters}
              visibleColumns={visibleKeys}
            />
            <ColumnSelectorButton
              columns={PROJECT_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Project
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load projects</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px]">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search projects..."
                  value={search}
                  onChange={(e) => resetPage(setSearch)(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) =>
                  resetPage(setStatusFilter)(e.target.value as StatusFilter)
                }
                className="w-40"
              >
                <option value="">All Status</option>
                <option value="PLANNING">Planning</option>
                <option value="ACTIVE">Active</option>
                <option value="ON_HOLD">On Hold</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </Select>
              <Select
                value={typeFilter}
                onChange={(e) => resetPage(setTypeFilter)(e.target.value)}
                className="w-40"
              >
                <option value="">All Types</option>
                {projectTypeOptions
                  .filter((t) => t.isActive)
                  .map((t) => (
                    <option key={t.id} value={t.value}>
                      {t.label}
                    </option>
                  ))}
              </Select>
              <Select
                value={companyFilter}
                onChange={(e) =>
                  resetPage(setCompanyFilter)(e.target.value)
                }
                className="w-44"
              >
                <option value="">All Companies</option>
                {allCompanies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
              <Select
                value={productFilter}
                onChange={(e) =>
                  resetPage(setProductFilter)(e.target.value)
                }
                className="w-44"
              >
                <option value="">All Products</option>
                {allProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </div>

            {/* Grid view */}
            {isGrid ? (
              <div>
                {isLoading ? (
                  <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      strokeWidth={1.5}
                    />
                    Loading projects…
                  </div>
                ) : projects.length === 0 ? (
                  <div className="py-16 text-center text-sm text-muted-foreground">
                    No projects match the current filters.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {projects.map((p) => (
                      <ProjectCard
                        key={p.id}
                        project={p}
                        typeLabel={p.type ? typeLabelByValue.get(p.type) : undefined}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Table view */
              <div
                className="overflow-hidden rounded-xl border bg-card"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      strokeWidth={1.5}
                    />
                    Loading projects…
                  </div>
                ) : projects.length === 0 ? (
                  <div className="py-16 text-center text-sm text-muted-foreground">
                    No projects match the current filters.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <tr>
                        {isVisible("name") && (
                          <th className="px-6 py-3">Name + Code</th>
                        )}
                        {isVisible("type") && (
                          <th className="px-6 py-3">Type</th>
                        )}
                        {isVisible("companyName") && (
                          <th className="px-6 py-3">Company</th>
                        )}
                        {isVisible("productName") && (
                          <th className="px-6 py-3">Product</th>
                        )}
                        {isVisible("status") && (
                          <th className="px-6 py-3">Status</th>
                        )}
                        {isVisible("progress") && (
                          <th className="px-6 py-3">Progress</th>
                        )}
                        {isVisible("managerName") && (
                          <th className="px-6 py-3">Manager</th>
                        )}
                        <th className="px-6 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {projects.map((p) => (
                        <ProjectRow
                          key={p.id}
                          project={p}
                          onDelete={handleDelete}
                          isVisible={isVisible}
                          typeLabel={p.type ? typeLabelByValue.get(p.type) : undefined}
                        />
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Pagination footer */}
                <div data-pagination="true" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '9px 14px',
                  borderTop: '1px solid hsl(210,30%,85%)',
                  background: 'hsl(210,30%,97%)',
                  fontSize: '12px',
                  color: 'hsl(215,16%,47%)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>
                      Showing {total === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total} entries
                    </span>
                    <span style={{ marginLeft: '8px' }}>Show</span>
                    <select
                      value={limit}
                      onChange={e => { setLimit(Number(e.target.value)); setPage(1) }}
                      style={{
                        height: '26px',
                        padding: '0 4px',
                        border: '1px solid hsl(210,30%,85%)',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: 'white',
                        cursor: 'pointer',
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span>entries</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button
                      onClick={() => setPage(p => p - 1)}
                      disabled={page === 1}
                      style={{
                        width: '28px', height: '28px',
                        border: '1px solid hsl(210,30%,85%)',
                        borderRadius: '4px',
                        background: 'white',
                        cursor: page === 1 ? 'not-allowed' : 'pointer',
                        opacity: page === 1 ? 0.4 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >‹</button>
                    <span style={{ padding: '0 10px' }}>
                      {Math.ceil(total / limit) === 0 ? '0' : page} / {Math.ceil(total / limit) || 1}
                    </span>
                    <button
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= Math.ceil(total / limit)}
                      style={{
                        width: '28px', height: '28px',
                        border: '1px solid hsl(210,30%,85%)',
                        borderRadius: '4px',
                        background: 'white',
                        cursor: page >= Math.ceil(total / limit) ? 'not-allowed' : 'pointer',
                        opacity: page >= Math.ceil(total / limit) ? 0.4 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >›</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <NewProjectDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />

        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete project?"
          description={
            deleteTarget
              ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Projects list.`
              : ""
          }
          onConfirm={confirmDelete}
        />
      </div>
    </AppLayout>
  );
}
